import React, { useState } from 'react';
import { CheckCircle2, Star, BadgeCheck, X, ChevronLeft, ChevronRight } from 'lucide-react';

export const References = () => {
  const projects = [
    {
      id: 1,
      title: "Realizace základové desky Veselí nad Moravou",
      subtitle: "Základová deska rodinného domu – kompletní realizace",
      description: "Pro investora jsme realizovali základovou desku pro rodinný dům v nové stavební lokalitě. Stavba probíhala od přípravy základových pasů až po finální betonáž základové desky.",
      images: [
        "https://customer-assets.emergentagent.com/job_brno-rekonstrukce/artifacts/mwyn4imr_1.jpg",
        "https://customer-assets.emergentagent.com/job_brno-rekonstrukce/artifacts/v5fwk9b4_2.jpg",
        "https://customer-assets.emergentagent.com/job_brno-rekonstrukce/artifacts/sym69tgl_3.jpg",
        "https://customer-assets.emergentagent.com/job_brno-rekonstrukce/artifacts/cf2fujr0_4.jpg"
      ],
      workItems: [
        "Zaměření a příprava staveniště",
        "Výkopové práce pro základové pásy",
        "Betonáž základových pásů",
        "Vyzdění základů z betonových tvárnic",
        "Provedení prostupů pro inženýrské sítě",
        "Zásypy a hutnění podkladních vrstev",
        "Armování základové desky",
        "Betonáž a finální srovnání povrchu"
      ],
      conclusion: "Celá základová konstrukce byla provedena s důrazem na přesnost, statickou stabilitu a kvalitní přípravu pro další fáze výstavby rodinného domu."
    },
    {
      id: 2,
      title: "Demolice bytového jádra Hodonín",
      subtitle: "Demolice bytového jádra – příprava pro novou rekonstrukci",
      description: "Pro zákazníka jsme realizovali kompletní demolici původního bytového jádra v panelovém bytě v Hodoníně. Práce zahrnovaly odstranění původních konstrukcí, obkladů i zařizovacích předmětů a přípravu prostoru pro následnou rekonstrukci koupelny a toalety.",
      images: [
        "https://customer-assets.emergentagent.com/job_brno-rekonstrukce/artifacts/bew2uj4y_2.jpeg",
        "https://customer-assets.emergentagent.com/job_brno-rekonstrukce/artifacts/g5i002a2_3.jpeg"
      ],
      workItems: [
        "Zakrytí a zabezpečení společných prostor",
        "Odpojení a demontáž původního vybavení",
        "Bourací práce bytového jádra",
        "Odstranění obkladů, dlažby a původních rozvodů",
        "Odvoz a ekologická likvidace stavebního odpadu",
        "Vyčištění a příprava prostoru pro další stavební práce"
      ],
      conclusion: "Demolice proběhla rychle, čistě a s důrazem na minimální omezení okolních bytových jednotek. Výsledkem byl kompletně připravený prostor pro novou moderní realizaci.",
      customerReview: {
        text: "Skvělá domluva, dochvilnost, platil smluvený termín i cena, realizace zakázky v krátkém časovém horizontu. VM projekty a stavby mohu jedině doporučit. Děkuji!",
        rating: 5,
        verified: true,
        source: "Firmy.cz"
      }
    },
    {
      id: 3,
      title: "Základová deska pro zahradní domek – Moravany u Brna",
      subtitle: "Základová deska 5 × 2 metry – kompletní realizace",
      description: "V Moravanech u Brna jsme pro zákazníka zrealizovali základovou desku o rozměrech 5 × 2 metry určenou pro budoucí zahradní domek. Součástí realizace bylo kompletní provedení od přípravy staveniště až po finální betonáž.",
      images: [
        "https://customer-assets.emergentagent.com/job_brno-rekonstrukce/artifacts/yjwzx59o_%C4%8Dikov.jpeg"
      ],
      workItems: [
        "Zaměření a vytyčení stavby",
        "Šalování základové desky",
        "Navážka a hutnění štěrkového lože",
        "Armování kari sítí",
        "Betonáž a finální úprava povrchu"
      ],
      conclusion: "Výsledkem je kvalitní a pevná základová deska připravená pro bezproblémovou montáž zahradního domku."
    }
  ];

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState('');
  const [allImages, setAllImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Handle ESC key to close lightbox
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && lightboxOpen) {
        closeLightbox();
      }
      if (e.key === 'ArrowLeft' && lightboxOpen && allImages.length > 1) {
        goToPrevious();
      }
      if (e.key === 'ArrowRight' && lightboxOpen && allImages.length > 1) {
        goToNext();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [lightboxOpen, currentIndex, allImages]);

  const openLightbox = (image, projectImages) => {
    setCurrentImage(image);
    setAllImages(projectImages);
    setCurrentIndex(projectImages.indexOf(image));
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const goToPrevious = () => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : allImages.length - 1;
    setCurrentIndex(newIndex);
    setCurrentImage(allImages[newIndex]);
  };

  const goToNext = () => {
    const newIndex = currentIndex < allImages.length - 1 ? currentIndex + 1 : 0;
    setCurrentIndex(newIndex);
    setCurrentImage(allImages[newIndex]);
  };

  return (
    <section id="references" className="py-24 bg-bg-page">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <h2 className="heading-2 mb-6">
            REFERENCE
          </h2>
          <p className="body-medium text-text-secondary max-w-3xl mx-auto">
            Realizované projekty
          </p>
        </div>

        <div className="max-w-6xl mx-auto space-y-16">
          {projects.map((project) => (
            <div key={project.id} className="border-b border-border-light pb-16 last:border-b-0 last:pb-0">
              {/* Project Header */}
              <div className="mb-12 text-center">
                <h3 className="heading-4 text-brand-primary mb-4">
                  {project.title}
                </h3>
                <p className="heading-5 text-text-primary mb-6">
                  {project.subtitle}
                </p>
                <p className="body-medium text-text-secondary max-w-4xl mx-auto mb-8">
                  {project.description}
                </p>
              </div>

              {/* Work Items */}
              <div className="bg-white border border-border-light rounded-lg p-8 mb-12">
                <h4 className="heading-5 text-brand-primary mb-6">
                  Součástí realizace bylo:
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {project.workItems.map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle2 
                        className="text-brand-primary shrink-0 mt-1" 
                        size={20} 
                      />
                      <span className="body-small text-text-primary">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="body-medium text-text-secondary mt-8 pt-6 border-t border-border-light">
                  {project.conclusion}
                </p>
              </div>

              {/* Photo Gallery */}
              <div className={`grid gap-4 mb-8 ${project.images.length === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-2 md:grid-cols-4'}`}>
                {project.images.map((image, index) => (
                  <div 
                    key={index}
                    onClick={() => openLightbox(image, project.images)}
                    className="aspect-square overflow-hidden rounded-lg border-2 border-border-light hover:border-brand-primary transition-all duration-300 cursor-pointer group"
                  >
                    <img 
                      src={image} 
                      alt={`${project.title} - fáze ${index + 1}`}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                  </div>
                ))}
              </div>

              {/* Customer Review */}
              {project.customerReview && (
                <div className="bg-white border-2 border-brand-primary rounded-lg p-8 mt-8">
                  <div className="flex items-start gap-4">
                    <div className="flex-1">
                      {/* Stars */}
                      <div className="flex items-center gap-1 mb-4">
                        {[...Array(project.customerReview.rating)].map((_, index) => (
                          <Star 
                            key={index} 
                            className="text-yellow-400 fill-yellow-400" 
                            size={24}
                          />
                        ))}
                      </div>
                      
                      {/* Review Text */}
                      <p className="body-medium text-text-primary italic mb-4">
                        "{project.customerReview.text}"
                      </p>
                      
                      {/* Verified Badge */}
                      {project.customerReview.verified && (
                        <div className="flex items-center gap-2 text-brand-primary">
                          <BadgeCheck size={20} className="shrink-0" />
                          <span className="caption font-semibold">
                            Ověřeno {project.customerReview.source}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-brand-primary transition-colors z-50"
          >
            <X size={32} />
          </button>

          {/* Previous Button */}
          {allImages.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
              className="absolute left-4 text-white hover:text-brand-primary transition-colors z-50"
            >
              <ChevronLeft size={48} />
            </button>
          )}

          {/* Image */}
          <div 
            className="max-w-7xl max-h-[90vh] p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={currentImage} 
              alt="Zvětšený náhled"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
          </div>

          {/* Next Button */}
          {allImages.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              className="absolute right-4 text-white hover:text-brand-primary transition-colors z-50"
            >
              <ChevronRight size={48} />
            </button>
          )}

          {/* Image Counter */}
          {allImages.length > 1 && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white bg-black bg-opacity-50 px-4 py-2 rounded-full">
              {currentIndex + 1} / {allImages.length}
            </div>
          )}
        </div>
      )}
    </section>
  );
};
